import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import style from "./CategoryWheel.module.css";
import Icon from "@/ui/icon/Icon";
import Button from "@/ui/button/Button";
import { CATEGORY_VISUALS } from "@/features/courses/categoryVisuals";
import { localized } from "@/i18n/content";
import { useCurrentLanguage } from "@/i18n/useLocalized";

// The icon and the colour token come from the shared visual map so the wheel
// cannot drift from the /courses category cards; only `name` and `desc` are
// landing-page copy, which /courses has no equivalent of. They are translation
// keys, run through `t()` where they are shown — a constant holding the text
// itself would keep the language the page loaded in.
const CATEGORIES = [
	{
		...CATEGORY_VISUALS.biznis,
		name: "landing:wheel.categories.biznis.name",
		desc: "landing:wheel.categories.biznis.desc",
	},
	{
		...CATEGORY_VISUALS.tehnologija,
		name: "landing:wheel.categories.tehnologija.name",
		desc: "landing:wheel.categories.tehnologija.desc",
	},
	{
		...CATEGORY_VISUALS.kreativa,
		name: "landing:wheel.categories.kreativa.name",
		desc: "landing:wheel.categories.kreativa.desc",
	},
];

// The colour is a runtime value, so it rides in on a custom property rather
// than a hardcoded hex.
function CategoryIcon({ category }) {
	return (
		<span
			className={style.catIcon}
			style={{ "--cat-color": `var(${category.rgbVar})` }}
		>
			<Icon icon={category.icon} />
		</span>
	);
}

const CHIP_COUNT = 4;
const STEP = 360 / CATEGORIES.length;

function CategoryWheel({ courses, isPending }) {
	const { t } = useTranslation();
	const language = useCurrentLanguage();
	const [angle, setAngle] = useState(0);
	// Flips the phase attribute on every advance, which is what restarts the
	// hub's sweep. A counter rather than `selected`, whose 0,1,2,0 cycle leaves
	// the parity unchanged on the last step.
	const [tick, setTick] = useState(0);

	// The hub's sweep drives the rotation rather than a timer beside it. It is
	// the thing the visitor is watching, it pauses under the pointer in CSS
	// (WCAG 2.2.2), and a separate clock could only ever agree with it by
	// accident — pause one and not the other and the wheel turns while the
	// circle sits half full. Reduced motion and a chosen category both remove
	// the animation, which is also how the rotation stops.
	const onSweepEnd = (event) => {
		if (event.pseudoElement !== "::after") return;
		setAngle((a) => a - STEP);
		setTick((t) => t + 1);
	};
	// Once the visitor has picked a category the rotation stops for good, so
	// the CTA never repoints under someone who is reading it (WCAG 3.2.5).
	const [userEngaged, setUserEngaged] = useState(false);

	// Everything the panel shows is derivable from /courses/all/, which the
	// landing page already fetches — course count, total duration, distinct
	// lecturers and the notable course names. No extra request, no aggregate
	// endpoint.
	const stats = useMemo(() => {
		const byCategory = {};

		for (const course of courses ?? []) {
			for (const category of course?.categories ?? []) {
				if (!category?.slug) continue;

				const entry = (byCategory[category.slug] ??= {
					count: 0,
					seconds: 0,
					lecturers: new Set(),
					courses: [],
				});

				entry.count += 1;
				entry.seconds += Number(course.duration) || 0;
				for (const lecturer of course?.lecturers ?? []) {
					if (lecturer?.name) entry.lecturers.add(lecturer.name);
				}
				if (course?.name) {
					entry.courses.push({
						name: localized(course, "name", language),
						likes: Number(course.like_count) || 0,
					});
				}
			}
		}

		return Object.fromEntries(
			Object.entries(byCategory).map(([slug, entry]) => [
				slug,
				{
					count: entry.count,
					hours: Math.round(entry.seconds / 3600),
					lecturers: entry.lecturers.size,
					// The best-liked few, as a sample of what the category holds.
					topCourses: entry.courses
						.sort((a, b) => b.likes - a.likes)
						.slice(0, CHIP_COUNT)
						.map((c) => c.name),
				},
			])
		);
	}, [courses, language]);

	// An empty catalogue means "we don't know yet",
	// not "this category has no courses".
	const unknown = isPending || (courses?.length ?? 0) === 0;
	const statsFor = (slug) =>
		stats[slug] ?? { count: 0, hours: 0, lecturers: 0, topCourses: [] };
	const countFor = (slug) => (unknown ? "—" : statsFor(slug).count);

	const selected =
		(((-angle / STEP) % CATEGORIES.length) + CATEGORIES.length) %
		CATEGORIES.length;
	const active = CATEGORIES[selected];

	const selectIndex = (index) => {
		setUserEngaged(true);
		setAngle((current) => {
			const now =
				(((-current / STEP) % CATEGORIES.length) + CATEGORIES.length) %
				CATEGORIES.length;
			const delta = (index - now + CATEGORIES.length) % CATEGORIES.length;
			return delta === 0 ? current : current - delta * STEP;
		});
	};

	return (
		<section className={style.section}>
			<div className={style.inner}>
				<div className={style.copy}>
					<div className={style.eyebrow}>{t("landing:wheel.eyebrow")}</div>
					<h2 className={style.title}>{t("landing:wheel.title")}</h2>

					<div className={style.pillRow}>
						{CATEGORIES.map((category, index) => (
							<button
								key={category.slug}
								type="button"
								onClick={() => selectIndex(index)}
								aria-pressed={index === selected}
								className={`${style.cardButton} ${
									style.pill
								} ${index === selected ? style.pillOn : ""}`}
							>
								<CategoryIcon category={category} />
								<span className={style.pillName}>
									{t(category.name)}
								</span>
							</button>
						))}
					</div>

					<div className={style.detail}>
						<p className={style.desc}>{t(active.desc)}</p>
						<div className={style.meta}>
							<span>
								<Icon icon="fa-layer-group" />
								{t("landing:wheel.courses", {
									count: statsFor(active.slug).count,
									value: countFor(active.slug),
								})}
							</span>
							<span>
								<Icon icon="fa-timer" />
								{t("landing:wheel.hours", {
									value: unknown
										? "—"
										: statsFor(active.slug).hours,
								})}
							</span>
							<span>
								<Icon icon="fa-user-group" />
								{t("landing:wheel.lecturers", {
									count: statsFor(active.slug).lecturers,
									value: unknown
										? "—"
										: statsFor(active.slug).lecturers,
								})}
							</span>
						</div>

						{!unknown &&
							statsFor(active.slug).topCourses.length > 0 && (
								<div className={style.chips}>
									{statsFor(active.slug).topCourses.map(
										(name) => (
											<span
												key={name}
												className={style.chip}
											>
												{name}
											</span>
										)
									)}
								</div>
							)}
					</div>

					{/* The outline button the FAQ and the hero end on, so
					    every secondary call to action on the page is the same
					    button. */}
					<Button
						type="outline"
						to={`/courses?category=${active.slug}`}
						className={style.cta}
					>
						<span>{t("landing:wheel.cta")}</span>
						<Icon icon="fa-arrow-right" />
					</Button>
				</div>

				<div className={style.wheelHolder}>
					{/* The big dashed ring the wheel has always sat on, with
					    three soft primary-tinted discs on the wheel's own centre
					    behind it, so the middle is not a hole. They stack, so the
					    tint deepens a step at a time towards the centre. */}
					<div className={style.rings} aria-hidden="true">
						{/* The innermost dashed circle doubles as the countdown
						    to the next turn: its fill sweeps round over the
						    rotation interval. The phase attribute alternates on
						    every advance so the sweep restarts with it, and is
						    dropped once the visitor picks a category — with no
						    attribute there is no animation, and the circle
						    rests at its unfilled tint. */}
						<div
							className={style.ring}
							data-phase={userEngaged ? undefined : tick % 2}
							onAnimationEnd={onSweepEnd}
						></div>
						<div
							className={`${style.disc} ${style.discOuter}`}
						></div>
						<div className={`${style.disc} ${style.discMid}`}></div>
						<div
							className={`${style.disc} ${style.discInner}`}
						></div>
					</div>

					<div
						className={style.wheel}
						style={{ transform: `rotate(${angle - 90}deg)` }}
					>
						{CATEGORIES.map((category, index) => (
							<div
								key={category.slug}
								className={style.slot}
								style={{
									transform: `rotate(${index * STEP}deg)`,
								}}
							>
								<div
									className={`${style.card} ${
										index === selected ? style.cardOn : ""
									}`}
									data-category={category.slug}
									style={{
										transform: `rotate(${-(
											angle -
											90 +
											index * STEP
										)}deg)`,
									}}
								>
									<div
										className={style.shapeLayer}
										aria-hidden="true"
									>
										<div className={style.shape}></div>
									</div>
									<button
										type="button"
										onClick={() => selectIndex(index)}
										aria-pressed={index === selected}
										className={style.cardButton}
									>
										<span className={style.cardHead}>
											<CategoryIcon category={category} />
											<span className={style.cardName}>
												{t(category.name)}
											</span>
										</span>
										<span className={style.cardCount}>
											<span>
												{t("landing:wheel.cardCount", {
													count: statsFor(category.slug)
														.count,
												})}
											</span>
											<strong>
												{countFor(category.slug)}
											</strong>
										</span>
									</button>
								</div>
							</div>
						))}
					</div>
					<div className={style.fadeBottom}></div>
					<div className={style.fadeRight}></div>
				</div>
			</div>
		</section>
	);
}

export default CategoryWheel;
